import { css } from '@emotion/react';
import { Stack, SxProps } from '@mui/material';
import { PropsWithChildren, useMemo } from 'react';
import { FormState, useFormContext } from 'react-hook-form';
import { ErrorText, FormLoadingButton } from '../../../components/Form/FormComponents';
import { FormFieldFactory } from '../../../components/Form/FormFieldFactory';
import { simpleTextField } from '../../../data-fields/CommonFields';
import { KpiCollectionConfiguration } from '../../../schemas/KpiCollectionConfiguration.schema';
import { useSaveKpiCollectionConfig } from './hooks/useSaveKpiCollectionConfig';

const FieldStyles = css`
  min-width: 10rem;
  overflow-x: clip;
  & * {
    max-width: 100%;
  }
  &&& .MuiInputBase-root input {
    font-size: 1.25rem;
    color: var(--mui-palette-secondary-main);
    @supports (field-sizing: content) {
      field-sizing: content;
    }
  }
`;

function DCConfigNameField() {
  const { formState } = useFormContext<KpiCollectionConfiguration>();

  const nameField = useMemo(
    () =>
      simpleTextField({
        key: 'name',
        label: '',
        required: true,
        placeholder: 'Configuration Name',
        autoFocus: true,
      }),
    []
  );

  return (
    <div css={FieldStyles}>
      <div>
        <FormFieldFactory formField={nameField} />
        <FloatingErrorText sx={{ mt: '-0.75rem' }}>{getFieldError(formState, 'name')}</FloatingErrorText>
      </div>
    </div>
  );
}

export function DCConfigFormHeader() {
  const { formState } = useFormContext<KpiCollectionConfiguration>();
  const { save, isUpdate, isPending } = useSaveKpiCollectionConfig();

  return (
    <Stack display='grid' gridTemplateColumns='1fr auto' alignItems='center' gap='1rem'>
      <DCConfigNameField />

      <Stack alignItems='flex-end'>
        <FormLoadingButton
          onClick={save}
          disabled={isPending || !formState.isDirty}
          loading={isPending}
          size='medium'
        >
          {isUpdate ? 'Update Configuration' : 'Save Configuration'}
        </FormLoadingButton>
        <FloatingErrorText>{getFieldError(formState, 'kpiIds')}</FloatingErrorText>
      </Stack>
    </Stack>
  );
}

function FloatingErrorText({ children, sx = {} }: PropsWithChildren & { sx?: SxProps }) {
  return (
    <ErrorText sx={{ height: 0, overflow: 'visible', transform: 'translateY(0.5rem)', ...sx }}>
      {children}
    </ErrorText>
  );
}

function getFieldError(
  formState: FormState<KpiCollectionConfiguration>,
  field: keyof KpiCollectionConfiguration
): string | undefined {
  return formState.isSubmitted ? formState.errors[field]?.message : undefined;
}
